import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import { observer } from 'mobx-react'

import { RentalDetailsCardModalTypes } from 'Modules/rentals/components/RentalDetailsCard/RentalDetailsCard.types'
import {
  getEarlyUpgradePath,
  getProlongPath,
  getRepairUpgradePath,
  getUpgradePath,
} from 'Modules/rentals/utils/rental.utils'

import { CardActions } from 'Components/CardActions/CardActions'
import { useToast } from 'Components/Toast/useToast'

import { LostOrStolenModal } from 'Modals/LostOrStolenModal/LostOrStolenModal'
import { LostOrStolenModalTypesEnum } from 'Modals/LostOrStolenModal/LostOrStolenModal.types'
import { ReplaceBrokenDeviceModal } from 'Modals/ReplaceBrokenDeviceModal/ReplaceBrokenDeviceModal'

import { useStore } from 'Stores/index'
import { OriginRouteTypeEnum } from 'Stores/orderStore/orderStore.types'

import { useModal } from 'Hooks'

import {
  E2E_LOST_STOLEN_DEVICE_ACTION,
  E2E_ORDER_EARLY_UGPRADE_ACTION,
  E2E_ORDER_PROLONG_ACTION,
  E2E_ORDER_UPGRADE_ACTION,
  E2E_REPLACE_BROKEN_DEVICE_ACTION,
} from 'Constants/e2e'
import type {
  getRental,
  getRental_rental,
  getRentalVariables,
} from 'Constants/graphql/queries/detail/__generated__/getRental'
import { GET_RENTAL } from 'Constants/graphql/queries/detail/GetRental'

import { iconLostDevice } from 'Icons/iconLostDevice'
import { iconRepairDevice } from 'Icons/iconRepairDevice'
import { iconUpgradeDevice } from 'Icons/iconUpgradeDevice'

import type { DashboardDevicesActionsProps } from './DashboardDevicesActions.types'

export const DashboardDevicesActions = observer(
  (props: DashboardDevicesActionsProps) => {
    const { rental, refetch } = props
    const { orderStore } = useStore()
    const { setOriginRoute } = orderStore

    const navigate = useNavigate()
    const location = useLocation()
    const { employeeId } = useParams<{
      employeeId?: string
    }>()

    const { setModal } = useModal()
    const { showToast } = useToast()
    const intl = useIntl()

    const [fetchRental, { loading: isFetchingRental }] = useLazyQuery<
      getRental,
      getRentalVariables
    >(GET_RENTAL)

    // The lost/stolen and replace-broken modals require the full rental (saved
    // office/delivery addresses, device details, action flags, child rentals).
    // The dashboard list intentionally does not fetch all of that per rental
    // (SM-2371), so fetch the complete rental on demand when a modal opens —
    // the same GET_RENTAL the other two modal entry points use — and hand the
    // modal that full object rather than a partial one. On failure we surface a
    // toast (useLazyQuery's default errorPolicy routes GraphQL errors here with
    // data undefined) instead of failing silently, and an in-flight guard keeps
    // a double-click from firing a second request.
    const fetchFullRental =
      useCallback(async (): Promise<getRental_rental | null> => {
        if (isFetchingRental) {
          return null
        }

        const { data } = await fetchRental({
          variables: {
            employeeId: employeeId ?? null,
            isOrganisationView: false,
            organisationId: null,
            rentalID: rental.id,
          },
        })

        const fullRental = data?.rental ?? null

        if (!fullRental) {
          showToast({
            content: intl.formatMessage({ id: 'GenericErrorMessage' }),
            variant: 'error',
          })
        }

        return fullRental
      }, [employeeId, fetchRental, intl, isFetchingRental, rental.id, showToast])

    const handleOpenReplaceBrokenDeviceModal = useCallback(
      async (isReplaceFromDepot: boolean) => {
        const fullRental = await fetchFullRental()

        if (!fullRental) {
          return
        }

        setModal({
          component: ReplaceBrokenDeviceModal,
          isOpen: true,
          props: {
            isReplaceFromDepot,
            onSubmit: refetch,
            rental: fullRental,
          },
        })
      },
      [fetchFullRental, refetch, setModal],
    )

    const handleRepairUpgrade = useCallback(() => {
      setOriginRoute({
        type: OriginRouteTypeEnum.Rental,
        url: location.pathname,
      })

      const path = getRepairUpgradePath(
        false,
        rental as getRental_rental,
        employeeId,
      )

      navigate(path)
    }, [setOriginRoute, location.pathname, rental, employeeId, navigate])

    const handleOpenLostOrStolenModal = useCallback(async () => {
      const fullRental = await fetchFullRental()

      if (!fullRental) {
        return
      }

      setModal({
        component: LostOrStolenModal,
        isOpen: true,
        props: {
          onSubmit: refetch,
          rental: fullRental,
          type: LostOrStolenModalTypesEnum.EMPLOYEE,
        },
      })
    }, [fetchFullRental, refetch, setModal])

    const handleEarlyUpgrade = useCallback(() => {
      setOriginRoute({
        type: OriginRouteTypeEnum.Rental,
        url: location.pathname,
      })

      const path = getEarlyUpgradePath(
        false,
        rental as getRental_rental,
        employeeId,
      )

      navigate(path)
    }, [employeeId, location.pathname, navigate, rental, setOriginRoute])

    const handleUpgrade = useCallback(() => {
      setOriginRoute({
        type: OriginRouteTypeEnum.Rental,
        url: location.pathname,
      })

      const path = getUpgradePath(false, rental as getRental_rental, employeeId)

      navigate(path)
    }, [employeeId, location.pathname, navigate, rental, setOriginRoute])

    const handleReplaceBrokenDevice = useCallback(
      (isReplaceFromDepot: boolean) => {
        const isRepairUpgradeAvailable =
          rental.actions.showRepairUpgradeAction &&
          rental.actions.showReplacementOrderFlow

        if (isRepairUpgradeAvailable) {
          handleRepairUpgrade()
        } else {
          handleOpenReplaceBrokenDeviceModal(isReplaceFromDepot)
        }
      },
      [
        rental.actions.showRepairUpgradeAction,
        rental.actions.showReplacementOrderFlow,
        handleRepairUpgrade,
        handleOpenReplaceBrokenDeviceModal,
      ],
    )

    const handleProlong = useCallback(() => {
      setOriginRoute({
        type: OriginRouteTypeEnum.Rental,
        url: location.pathname,
      })

      const path = getProlongPath(false, rental as getRental_rental, employeeId)

      navigate(path)
    }, [employeeId, location.pathname, navigate, rental, setOriginRoute])

    const actions = useMemo(
      () => [
        {
          e2eSelector: E2E_LOST_STOLEN_DEVICE_ACTION,
          icon: iconLostDevice,
          id: RentalDetailsCardModalTypes.LostOrStolenModal,
          isDisabled: isFetchingRental,
          isHidden: !rental.actions.showLostOrStolenAction,
          name: 'Device_lost_stolen',
          onActionClick: handleOpenLostOrStolenModal,
        },
        {
          e2eSelector: E2E_REPLACE_BROKEN_DEVICE_ACTION,
          icon: iconRepairDevice,
          id: RentalDetailsCardModalTypes.ReplaceBrokenDevice,
          isDisabled: isFetchingRental,
          isHidden: !rental.actions.showReplaceBrokenDeviceAction,
          name: 'Replace_broken_device',
          onActionClick: () => handleReplaceBrokenDevice(false),
        },
        {
          e2eSelector: E2E_ORDER_EARLY_UGPRADE_ACTION,
          icon: iconUpgradeDevice,
          id: RentalDetailsCardModalTypes.EarlyUpgradeDevice,
          isHidden: !rental.actions.showEarlyUpgradeAction,
          name: 'Early_upgrade_device',
          onActionClick: handleEarlyUpgrade,
        },
        {
          e2eSelector: E2E_ORDER_PROLONG_ACTION,
          icon: iconUpgradeDevice,
          id: RentalDetailsCardModalTypes.ProlongDevice,
          isHidden: !rental.actions.showDeviceProlongingAction,
          name: 'Extend_rental',
          onActionClick: handleProlong,
        },
        {
          e2eSelector: E2E_ORDER_UPGRADE_ACTION,
          icon: iconUpgradeDevice,
          id: RentalDetailsCardModalTypes.RentalCompleteUpgradeDevice,
          isHidden: !rental.actions.showStandardUpgradeAction,
          name: 'Upgrade_device',
          onActionClick: handleUpgrade,
        },
      ],
      [
        isFetchingRental,
        rental.actions.showLostOrStolenAction,
        rental.actions.showReplaceBrokenDeviceAction,
        rental.actions.showEarlyUpgradeAction,
        rental.actions.showDeviceProlongingAction,
        rental.actions.showStandardUpgradeAction,
        handleOpenLostOrStolenModal,
        handleReplaceBrokenDevice,
        handleEarlyUpgrade,
        handleProlong,
        handleUpgrade,
      ],
    )

    return (
      <CardActions actions={actions} customColumnsCount={4} type="rental" />
    )
  },
)
